// Analytics view — workbook KPIs, 6-month delivery outlook, top
// customers by value, workbook by status, and the upcoming-spool
// pipeline. Values are split across spools so the workshop sees
// partial credit on jobs in flight rather than a binary delivered /
// not-delivered flip on the job status.
//
// Apportionment model: each spool inherits an equal share of its
// parent job's order value (orderValue / spool count). Each spool's
// share contributes `share * stageCompletionPct` to the "delivered"
// totals; the remainder is "remaining". Month bucketing uses
// spool.dueDate (falling back to job.dueDate when the spool has no
// own due date) so a job whose spools deliver across multiple
// months splits its value across those months. The Completed-MTD
// KPI looks at spools fully complete with their latest stage
// doneDate inside the current month, not the parent job's status.
//
// Cancelled jobs are excluded from every metric so the "active
// workbook" framing matches the prototype. Jobs without an
// orderValue contribute zero to value totals but still appear in
// the count nudges (so the workshop can see when data is missing).
import { useMemo } from "react";
import type { JobDto } from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "../components/StagesProvider";
import { isoToday } from "../lib/dates";
import {
  compute,
  monthKey,
  statusClass,
  type Month,
} from "./analyticsModel";

interface Props {
  jobs: JobDto[];
  onOpenJob(id: number): void;
}

// AUD formatters. Compact shows $1.23M / $456k / $7,890; full
// renders the whole number with grouping for "$1,234,567".
function fmtCompact(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString("en-AU")}`;
}

function fmtFull(v: number): string {
  return `$${Math.round(v).toLocaleString("en-AU")}`;
}

export function AnalyticsView({ jobs, onOpenJob }: Props) {
  const catalogue = useActiveStages();
  const c = useMemo(() => compute(jobs, catalogue), [jobs, catalogue]);
  // Completed-MTD progress is value-weighted against projCur — gives
  // a more honest "X% of this month's value is done" signal than
  // counting completed jobs, since one big completed job and ten
  // small ones can land on the same count but very different values.
  const completionPct = c.projCur
    ? Math.round((c.completedMTD / c.projCur) * 100)
    : 0;
  const nextMonthSpools = c.months.find((m) => m.k === c.nextKey)?.spoolCount ?? 0;
  // Local-zone YYYY-MM-DD. spool/job dueDate are calendar dates
  // entered in the workshop's timezone, so UTC slicing was
  // mis-flagging rows as overdue around UTC midnight on the wrong
  // side of date.
  const todayIso = isoToday();

  return (
    <div className="an-wrap">
      <div className="an-kpi-grid">
        <div className="an-kpi feature">
          <div className="an-kpi-label">
            Total Workbook Value
            <span className="an-pill">{c.active.length} JOBS</span>
          </div>
          <div className="an-kpi-val">
            <span className="an-curr">AUD</span>
            {fmtCompact(c.totalWorkbook)}
          </div>
          <div className="an-kpi-sub">
            {fmtFull(c.totalWorkbook)} across all active jobs
            {c.missingValue > 0 ? ` · ${c.missingValue} job${c.missingValue === 1 ? "" : "s"} missing value` : ""}
          </div>
          <div className="an-kpi-bar" />
        </div>
        <div className="an-kpi green">
          <div className="an-kpi-label">
            Completed MTD <span className="an-pill">{c.curLabel}</span>
          </div>
          <div className="an-kpi-val">
            <span className="an-curr">AUD</span>
            {fmtCompact(c.completedMTD)}
          </div>
          <div className="an-kpi-sub">
            {completionPct}% of this month's projection delivered
          </div>
          <div className="an-kpi-bar" />
        </div>
        <div className="an-kpi">
          <div className="an-kpi-label">
            Projected · This Month <span className="an-pill">{c.curShort}</span>
          </div>
          <div className="an-kpi-val">
            <span className="an-curr">AUD</span>
            {fmtCompact(c.projCur)}
          </div>
          <div className="an-kpi-sub">
            {fmtCompact(c.remainingCur)} still to deliver · {c.curMonthSpools} spool{c.curMonthSpools === 1 ? "" : "s"} due
          </div>
          <div className="an-kpi-bar" />
        </div>
        <div className="an-kpi orange">
          <div className="an-kpi-label">
            Projected · Next Month <span className="an-pill">{c.nextShort}</span>
          </div>
          <div className="an-kpi-val">
            <span className="an-curr">AUD</span>
            {fmtCompact(c.projNext)}
          </div>
          <div className="an-kpi-sub">
            {nextMonthSpools} spool{nextMonthSpools === 1 ? "" : "s"} scheduled for delivery
          </div>
          <div className="an-kpi-bar" />
        </div>
      </div>

      <div className="an-card">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Six-month delivery outlook</div>
            <div className="an-card-sub">Workbook value scheduled to land in each month, by due date</div>
          </div>
          <div className="an-card-sub">Max {fmtCompact(c.maxMonth)} · scale per column</div>
        </div>
        <div className="an-card-body">
          <div className="an-chart">
            {c.months.map((m) => (
              <MonthBar key={m.k} month={m} max={c.maxMonth} />
            ))}
          </div>
        </div>
        <div className="an-legend">
          <span><span className="an-swatch completed" />Completed</span>
          <span><span className="an-swatch remaining" />Remaining this month</span>
          <span><span className="an-swatch projected" />Projected / future month</span>
          <span style={{ marginLeft: "auto", color: "var(--bsp-fg-subtle)" }}>
            Bars driven by spool due date + stage completion · cancelled jobs excluded
          </span>
        </div>
        <div style={{ height: 14 }} />
      </div>

      <div className="an-2col">
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Top customers by value</div>
              <div className="an-card-sub">Active workbook contribution — top {c.customers.length}</div>
            </div>
          </div>
          <table className="an-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th className="num">Jobs</th>
                <th className="num">Workbook</th>
                <th className="num">Completed</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {c.customers.length === 0 ? (
                <tr><td colSpan={5} className="an-empty">No customer data yet — add order values to jobs to populate.</td></tr>
              ) : (
                c.customers.map((cust) => {
                  const share = c.totalWorkbook ? Math.round((cust.total / c.totalWorkbook) * 100) : 0;
                  return (
                    <tr key={cust.name}>
                      <td>
                        <div style={{ fontWeight: 700, color: "var(--bsp-dark)" }}>{cust.name}</div>
                        <div className="an-share-bar"><span style={{ width: `${share}%` }} /></div>
                      </td>
                      <td className="num">{cust.count}</td>
                      <td className="num">{fmtCompact(cust.total)}</td>
                      <td className="num" style={{ color: "var(--bsp-green-fg)" }}>
                        {cust.completed ? fmtCompact(cust.completed) : "—"}
                      </td>
                      <td className="num">{share}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="an-card">
          <div className="an-card-head">
            <div>
              <div className="an-card-title">Workbook by status</div>
              <div className="an-card-sub">Where the value sits in the pipeline</div>
            </div>
          </div>
          <table className="an-table">
            <thead>
              <tr>
                <th>Status</th>
                <th className="num">Jobs</th>
                <th className="num">Value</th>
                <th className="num">Share</th>
              </tr>
            </thead>
            <tbody>
              {c.statuses.length === 0 ? (
                <tr><td colSpan={4} className="an-empty">No jobs in the workbook yet.</td></tr>
              ) : (
                <>
                  {c.statuses.map((row) => {
                    const share = c.totalWorkbook ? Math.round((row.total / c.totalWorkbook) * 100) : 0;
                    return (
                      <tr key={row.s}>
                        <td>
                          <span className={`an-status-dot ${statusClass(row.s)}`} />
                          <span style={{ fontWeight: 600, color: "var(--bsp-dark)" }}>{row.s}</span>
                        </td>
                        <td className="num">{row.count}</td>
                        <td className="num">{fmtCompact(row.total)}</td>
                        <td className="num">{share}%</td>
                      </tr>
                    );
                  })}
                  <tr className="totals">
                    <td>Total active workbook</td>
                    <td className="num">{c.active.length}</td>
                    <td className="num">{fmtCompact(c.totalWorkbook)}</td>
                    <td className="num">100%</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="an-card">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Upcoming delivery — this month &amp; next</div>
            <div className="an-card-sub">
              {c.upcoming.length} spool{c.upcoming.length === 1 ? "" : "s"} due by {c.nextLabel}
            </div>
          </div>
        </div>
        <table className="an-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Spool / Customer</th>
              <th>Status</th>
              <th>Due</th>
              <th className="num">Progress</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {c.upcoming.length === 0 ? (
              <tr><td colSpan={6} className="an-empty">No spools due this month or next.</td></tr>
            ) : (
              c.upcoming.map((row, idx) => {
                const due = row.dueDate ? new Date(`${row.dueDate}T00:00:00`) : null;
                const dueLabel = due
                  ? due.toLocaleDateString("en-AU", { day: "2-digit", month: "short" })
                  : "—";
                const overdue = row.dueDate !== null
                  && row.dueDate < todayIso
                  && row.completionPct < 1;
                const pct = Math.round(row.completionPct * 100);
                return (
                  <tr
                    key={`${row.jobId}-${row.spoolNum}-${idx}`}
                    onClick={() => onOpenJob(row.jobId)}
                    style={{ cursor: "pointer" }}
                  >
                    <td><span className="proj-id">{row.jobNumber}</span></td>
                    <td>
                      <div style={{ fontWeight: 700, color: "var(--bsp-dark)" }}>
                        {row.spoolNum}
                        <span style={{ fontWeight: 500, color: "var(--bsp-fg-muted)", marginLeft: 6 }}>
                          · {row.jobName || "—"}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", fontFamily: "var(--bsp-font-mono)", letterSpacing: "0.02em" }}>
                        {row.customer || "—"}
                      </div>
                    </td>
                    <td>
                      <span className={`an-status-dot ${statusClass(row.status)}`} />
                      <span style={{ fontWeight: 600 }}>{row.status || "New"}</span>
                    </td>
                    <td style={{
                      fontFamily: "var(--bsp-font-mono)",
                      fontWeight: 600,
                      color: overdue ? "var(--bsp-red-fg)" : "var(--bsp-dark)",
                    }}>
                      {dueLabel}{overdue ? " · overdue" : ""}
                    </td>
                    <td className="num" style={{
                      color: pct === 100
                        ? "var(--bsp-green-fg)"
                        : pct > 0 ? "var(--bsp-blue)" : "var(--bsp-fg-subtle)",
                    }}>
                      {pct}%
                    </td>
                    <td className="num">
                      {row.share > 0
                        ? fmtCompact(row.share)
                        : <span style={{ color: "var(--bsp-fg-subtle)", fontWeight: 500 }}>no value</span>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MonthBar({ month, max }: { month: Month; max: number }) {
  const total = month.total;
  const segs = month.isFuture
    ? [{
        cls: "projected",
        h: total ? Math.max(4, Math.round((total / max) * 100)) : 0,
      }]
    : [
        total > 0 && month.completed > 0
          ? { cls: "completed", h: Math.max(2, Math.round((month.completed / max) * 100)) }
          : null,
        total > 0 && month.remaining > 0
          ? {
              cls: month.isPast ? "projected" : "remaining",
              h: Math.max(2, Math.round((month.remaining / max) * 100)),
            }
          : null,
      ].filter((x): x is { cls: string; h: number } => x !== null);

  return (
    <div className={`an-bar-col ${month.isCurrent ? "current" : ""}`}>
      <div className="an-bar-stack">
        {total > 0 && <div className="an-bar-val">{fmtCompact(total)}</div>}
        {segs.map((s, i) => (
          <div key={i} className={`an-bar-seg ${s.cls}`} style={{ height: `${s.h}%` }} />
        ))}
      </div>
      <div className="an-bar-meta">
        <div className="an-month">{month.label}</div>
        <div>{month.spoolCount} spool{month.spoolCount === 1 ? "" : "s"}</div>
      </div>
    </div>
  );
}
